import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGame } from '../context/GameContext';
import { useTranslation } from '../../../shared/i18n';
import { BackgroundWrapper, PageTransition, BackButton, AnimatedTitle, PageContainer } from '../components';
import { categoryData } from '../data/categories';
import * as S from './QuestionPage.styles';

// Import JSON files directly
import sabiduriaLugares from '../data/questions/sabiduria-lugares.json';
import sabiduriaVariedades from '../data/questions/sabiduria-variedades.json';
import sabiduriaPersonajes from '../data/questions/sabiduria-personajes.json';
import alianzaSiNosotros from '../data/questions/alianza-si-nosotros.json';
import alianzaCoincidimos from '../data/questions/alianza-coincidimos.json';
import alianzaArriesgamos from '../data/questions/alianza-arriesgamos.json';

// Map of question data
const questionDataMap = {
  'sabiduria-lugares': sabiduriaLugares,
  'sabiduria-variedades': sabiduriaVariedades,
  'sabiduria-personajes': sabiduriaPersonajes,
  'alianza-si-nosotros': alianzaSiNosotros,
  'alianza-coincidimos': alianzaCoincidimos,
  'alianza-arriesgamos': alianzaArriesgamos
};

function QuestionPage() {
  const { category, subcategory } = useParams();
  const navigate = useNavigate();
  const { markQuestionAsUsed, getUsedQuestions, resetSubcategory } = useGame();
  const t = useTranslation();
  
  const [question, setQuestion] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questionMarkedAsUsed, setQuestionMarkedAsUsed] = useState(false);
  const answerSectionRef = useRef(null);
  
  // Determine if this question type has a definitive answer
  const hasDefinitiveAnswer = question && 
    (question.type === 'multiple-choice' || 
     question.type === 'true-false' || 
     (!question.type && question.answer)); // Backward compatibility

  // Get subcategory info from categoryData
  const categoryInfo = categoryData[category];
  const subcategoryInfo = categoryInfo?.subcategories.find(sub => sub.key === subcategory);

  // Get dynamic text based on question type
  const getQuestionSectionTitle = () => {
    if (question?.type === 'prompt') {
      return t('conectados.question.prompt'); // "Consigna"
    }
    return t('conectados.question.question'); // "Pregunta"
  };

  const getOptionsSectionTitle = () => {
    if (question?.type === 'open-ended') {
      return t('conectados.question.whichVerse'); // "¿Qué versículo utilizarías?"
    }
    return t('conectados.question.options'); // "Opciones de respuesta:"
  };

  // Generate fixed options for true-false questions if they don't exist
  const getDisplayOptions = () => {
    if (question?.type === 'true-false' && (!question.options || question.options.length === 0)) {
      return [t('conectados.question.trueOption'), t('conectados.question.falseOption')];
    }
    return question?.options;
  };

  // Helper function to check if an option is the correct answer
  const isCorrectOption = (option, correctAnswer) => {
    if (!option || !correctAnswer) return false;
    
    // Direct match
    if (option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()) {
      return true;
    }
    
    // Check if the correct answer contains the option (for cases like "Moisés" vs "Moisés fue...")
    if (correctAnswer.trim().toLowerCase().includes(option.trim().toLowerCase())) {
      return true;
    }
    
    // Check if the option contains the correct answer
    if (option.trim().toLowerCase().includes(correctAnswer.trim().toLowerCase())) {
      return true;
    }
    
    return false;
  };

  const loadQuestion = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setQuestionMarkedAsUsed(false);
      
      // Get questions from the imported data
      const questionKey = `${category}-${subcategory}`;
      const questions = questionDataMap[questionKey];
      
      if (!questions) {
        throw new Error(t('conectados.question.error'));
      }
      
      // Get used questions for this subcategory
      const usedQuestionIds = getUsedQuestions(`${category}-${subcategory}`);
      
      // Filter out used questions
      let availableQuestions = questions.filter(q => !usedQuestionIds.includes(q.id));
      
      // If no questions available, reset the subcategory and use all questions again
      if (availableQuestions.length === 0) {
        resetSubcategory(`${category}-${subcategory}`);
        availableQuestions = questions;
      }
      
      // Select a random question from available ones
      const randomIndex = Math.floor(Math.random() * availableQuestions.length);
      const selectedQuestion = availableQuestions[randomIndex];
      
      setQuestion(selectedQuestion);
      setShowAnswer(false);
      setLoading(false);
      
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [category, subcategory]); // eslint-disable-line react-hooks/exhaustive-deps
  // Note: getUsedQuestions and resetSubcategory are intentionally excluded to prevent re-running loadQuestion when marking questions as used

  useEffect(() => {
    loadQuestion();
  }, [loadQuestion]);

  const handleShowAnswer = () => {
    setShowAnswer(true);
    
    // Mark question as used only when user actually sees the answer
    if (question && !questionMarkedAsUsed) {
      markQuestionAsUsed(`${category}-${subcategory}`, question.id);
      setQuestionMarkedAsUsed(true);
    }
    
    // Only scroll if answer section is significantly out of view
    // This prevents aggressive scrolling when user is already near the answer
    setTimeout(() => {
      if (answerSectionRef.current) {
        const rect = answerSectionRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        
        // Check if the answer section top is below the viewport
        // Only scroll if it's significantly cut off (more than 50% hidden)
        const isSignificantlyHidden = rect.top > viewportHeight * 0.7;
        
        if (isSignificantlyHidden) {
          answerSectionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }
    }, 150);
  };

  const handleEndTurn = () => {
    // Mark question as used before ending turn (for questions without answers)
    if (question && !questionMarkedAsUsed) {
      markQuestionAsUsed(`${category}-${subcategory}`, question.id);
      setQuestionMarkedAsUsed(true);
    }
    
    // Return to Conectados main page, keeping the used questions list
    navigate('/conectados');
  };

  if (loading) {
    return (
      <BackgroundWrapper>
        <PageContainer>
          <AnimatedTitle />
          
          <PageTransition>
            <S.LoadingContainer>{t('conectados.question.loading')}</S.LoadingContainer>
          </PageTransition>
        </PageContainer>
      </BackgroundWrapper>
    );
  }

  if (error) {
    return (
      <BackgroundWrapper>
        <PageContainer>
          <AnimatedTitle />
          
          <BackButton to={`/conectados/${category}`} />
          
          <PageTransition>
            <S.ErrorContainer>
              <h3>{t('conectados.question.error')}</h3>
              <p>{error}</p>
              <S.ActionButton onClick={loadQuestion}>{t('conectados.question.tryAgain')}</S.ActionButton>
            </S.ErrorContainer>
          </PageTransition>
        </PageContainer>
      </BackgroundWrapper>
    );
  }


  return (
    <BackgroundWrapper>
      <PageContainer>
        <AnimatedTitle />
        
        <BackButton to={`/conectados/${category}`} />
        
        <PageTransition>
          <S.QuestionCard translate="no">
            <S.CardHeader>
              {subcategoryInfo && (
                <S.SubcategoryImageContainer>
                  {subcategoryInfo.image ? (
                    <S.SubcategoryImage 
                      src={subcategoryInfo.image} 
                      alt={subcategoryInfo.title}
                    />
                  ) : (
                    <S.SubcategoryIconContainer>
                      {subcategoryInfo.titleIcon && <subcategoryInfo.titleIcon size={60} />}
                      <S.SubcategoryTitle>{subcategoryInfo.title}</S.SubcategoryTitle>
                    </S.SubcategoryIconContainer>
                  )}
                </S.SubcategoryImageContainer>
              )}
            </S.CardHeader>
            <S.QuestionSection>
              <S.SectionTitle>{getQuestionSectionTitle()}</S.SectionTitle>
              <S.QuestionText>{question.question}</S.QuestionText>
            </S.QuestionSection>
            
            {/* Show options if they exist or need to be generated */}
            {(() => {
              const displayOptions = getDisplayOptions();
              if (displayOptions && displayOptions.length > 0) {
                return (
                  <S.OptionsSection>
                    <S.OptionsSectionTitle>{getOptionsSectionTitle()}</S.OptionsSectionTitle>
                    <S.OptionsGrid 
                      useTwoColumns={question.type === 'multiple-choice' || !question.type}
                      isTrueFalse={question.type === 'true-false'}
                    >
                      {displayOptions.map((option, index) => {
                        const isCorrect = hasDefinitiveAnswer && isCorrectOption(option, question.answer);
                        return (
                          <S.OptionCard key={index} isCorrect={isCorrect} showAnswer={showAnswer}>
                            <S.OptionLabel isCorrect={isCorrect} showAnswer={showAnswer}>
                              {String.fromCharCode(65 + index)}
                            </S.OptionLabel>
                            {isCorrect && showAnswer && <S.CorrectIcon />}
                            <S.OptionText>{option}</S.OptionText>
                          </S.OptionCard>
                        );
                      })}
                    </S.OptionsGrid>
                  </S.OptionsSection>
                );
              }
              return null;
            })()}
            
            {/* Show answer section or end turn button based on question type */}
            {hasDefinitiveAnswer ? (
              // Questions with answers: show "Show Answer" button, then answer section
              !showAnswer ? (
                <S.ActionsContainer>
                  <S.ShowAnswerButton onClick={handleShowAnswer}>
                    {t('conectados.question.showAnswer')}
                  </S.ShowAnswerButton>
                </S.ActionsContainer>
              ) : (
                <S.AnswerSection ref={answerSectionRef}>
                  <S.AnswerSectionTitle>{t('conectados.question.answer')}</S.AnswerSectionTitle>
                  <S.AnswerText>{question.answer}</S.AnswerText>
                  {question.reference && (
                    <S.ReferenceText>{t('conectados.question.reference')} {question.reference}</S.ReferenceText>
                  )}
                  <S.ActionsContainer>
                    <S.EndTurnButton onClick={handleEndTurn}>
                      {t('conectados.question.endTurn')}
                    </S.EndTurnButton>
                  </S.ActionsContainer>
                </S.AnswerSection>
              )
            ) : (
              // Questions without definitive answers: show "End Turn" button directly
              <S.ActionsContainer>
                <S.EndTurnButton onClick={handleEndTurn}>
                  {t('conectados.question.endTurn')}
                </S.EndTurnButton>
              </S.ActionsContainer>
            )}
          </S.QuestionCard>
        </PageTransition>
      </PageContainer>
    </BackgroundWrapper>
  );
}

export default QuestionPage; 